"use client";

import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const EMAIL = "hello@believemy.com";
// Le siège social, tel qu'il figure aux mentions légales.
const ADDRESS = ["Believemy", "229 rue Saint-Honoré", "75001 Paris, France"];

const FIELD =
    "block w-full rounded-2xl border-0 bg-white px-4 py-3 text-sm text-slate-900 outline-hidden ring-1 ring-slate-900/10 placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/20 dark:bg-white/5 dark:text-white dark:ring-white/10 dark:placeholder:text-grayDark-500 dark:focus:ring-white/25";

/** Un autre moyen d'écrire : toute la ligne se clique pour copier l'adresse. */
function OtherWay({ icon, text, copied, onCopy, copyLabel, copiedLabel }) {
    return (
        <button
            type="button"
            onClick={onCopy}
            className="group flex w-full cursor-pointer items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-slate-100 dark:hover:bg-white/10"
        >
            <SolarIcon name={icon} className="shrink-0 text-base text-slate-400 dark:text-grayDark-400" />
            <span className="min-w-0 flex-1 text-sm text-slate-700 dark:text-grayDark-200">{text}</span>
            <span
                className={`flex shrink-0 items-center gap-1 text-xs font-medium transition-colors duration-150 ${
                    copied
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-slate-400 group-hover:text-slate-700 dark:text-grayDark-500 dark:group-hover:text-white"
                }`}
            >
                <SolarIcon name={copied ? "check-circle-linear" : "copy-linear"} className="text-sm" />
                {/* Les deux libellés occupent la même case : la largeur ne change
                    pas au clic, donc l'adresse ne se remet pas en page. */}
                <span className="grid">
                    <span className={`col-start-1 row-start-1 ${copied ? "invisible" : ""}`}>{copyLabel}</span>
                    <span className={`col-start-1 row-start-1 ${copied ? "" : "invisible"}`} aria-hidden={!copied}>
                        {copiedLabel}
                    </span>
                </span>
            </span>
        </button>
    );
}

function ContactForm({ user, onSent }) {
    const t = useTranslations("layout");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    // Ce qui vient d'être copié : "email", "address" ou null.
    const [copied, setCopied] = useState(null);
    const copyTimer = useRef(null);

    // Un membre connecté n'a pas à redonner son nom ni son adresse.
    const senderName = user ? [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email : name.trim();
    const senderEmail = user ? user.email : email.trim();

    const send = async () => {
        if (sending) return;
        if (!senderName || !senderEmail || !message.trim()) {
            toast.error(t("ContactModal_fill"));
            return;
        }
        setSending(true);
        try {
            const response = await fetch("/api/interface/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: senderName,
                    email: senderEmail,
                    message,
                    page: window.location.pathname,
                }),
            });
            if (!response.ok) throw new Error();
            toast.success(t("ContactModal_sent"));
            onSent();
        } catch {
            toast.error(t("ContactModal_error"));
        } finally {
            setSending(false);
        }
    };

    const copy = async (what, value) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(what);
            clearTimeout(copyTimer.current);
            copyTimer.current = setTimeout(() => setCopied(null), 1800);
        } catch {
            // Presse-papiers refusé : l'adresse reste affichée et sélectionnable.
        }
    };

    return (
        <div className="p-6">
            <h3 id="contact-modal-title" className="mb-1 text-lg font-bold text-slate-900 dark:text-white">
                {t("ContactModal_title")}
            </h3>
            <p className="mb-4 text-sm text-slate-500 dark:text-grayDark-400">{t("ContactModal_description")}</p>

            {!user && (
                <div className="mb-2 grid gap-2 sm:grid-cols-2">
                    <input
                        type="text"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        maxLength={120}
                        autoComplete="name"
                        aria-label={t("ContactModal_name")}
                        placeholder={t("ContactModal_name")}
                        className={FIELD}
                    />
                    <input
                        type="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        maxLength={200}
                        autoComplete="email"
                        aria-label={t("ContactModal_email")}
                        placeholder={t("ContactModal_email")}
                        className={FIELD}
                    />
                </div>
            )}

            {/* Le message et son envoi dans un même cadre, comme un champ de discussion. */}
            <div className="rounded-2xl bg-white ring-1 ring-slate-900/10 transition-shadow duration-150 focus-within:ring-2 focus-within:ring-slate-900/20 dark:bg-white/5 dark:ring-white/10 dark:focus-within:ring-white/25">
                <textarea
                    rows={5}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    maxLength={4000}
                    aria-label={t("ContactModal_message")}
                    placeholder={t("ContactModal_message")}
                    className="block w-full resize-none border-0 bg-transparent px-4 pb-1 pt-3.5 text-sm leading-relaxed text-slate-900 outline-hidden placeholder:text-slate-400 focus:ring-0 dark:text-white dark:placeholder:text-grayDark-500"
                />
                <div className="flex min-h-14 items-center justify-between gap-3 py-2 pl-4 pr-2">
                    <p className="mb-0 min-w-0 truncate text-xs text-slate-400 dark:text-grayDark-500">
                        {user ? t("ContactModal_replyTo", { email: user.email }) : ""}
                    </p>
                    <Button
                        small
                        isLoading={sending}
                        onClick={send}
                        className="!shrink-0 !rounded-full !bg-primary !text-white !font-medium !text-sm hover:!bg-primary-300"
                    >
                        {t("ContactModal_send")}
                    </Button>
                </div>
            </div>

            <p className="mb-1.5 mt-5 text-xs font-medium text-slate-500 dark:text-grayDark-400">
                {t("ContactModal_other")}
            </p>
            <div className="divide-y divide-slate-200/70 overflow-hidden rounded-2xl bg-slate-50 dark:divide-white/10 dark:bg-white/5">
                <OtherWay
                    icon="letter-linear"
                    text={EMAIL}
                    copied={copied === "email"}
                    onCopy={() => copy("email", EMAIL)}
                    copyLabel={t("ContactModal_copy")}
                    copiedLabel={t("ContactModal_copied")}
                />
                <OtherWay
                    icon="map-point-linear"
                    // Affichée sans le nom, pour tenir sur une ligne ; la copie le garde.
                    text={ADDRESS.slice(1).join(", ")}
                    copied={copied === "address"}
                    onCopy={() => copy("address", ADDRESS.join("\n"))}
                    copyLabel={t("ContactModal_copy")}
                    copiedLabel={t("ContactModal_copied")}
                />
            </div>
        </div>
    );
}

/**
 * « Contactez-nous » : un message écrit sur place, envoyé à l'équipe par
 * /api/interface/contact, puis l'adresse e-mail et l'adresse postale pour qui
 * préfère écrire autrement. Monté dans le layout racine : textes du namespace
 * « layout ».
 */
export default function ContactModal({ isOpen, onClose, user }) {
    return (
        <BottomSheetModal
            isOpen={isOpen}
            onClose={onClose}
            maxWidthClass="sm:max-w-lg"
            zIndexClass="z-[9999]"
            labelledBy="contact-modal-title"
            portal
        >
            <ContactForm user={user} onSent={onClose} />
        </BottomSheetModal>
    );
}
